import FocusTrap from "bootstrap/js/dist/util/focustrap.js";
import SelectorEngine from "bootstrap/js/dist/dom/selector-engine.js";

// Bootstrap owns each transition/backdrop. This coordinator owns the document
// lock and focus across dialogs, which Bootstrap otherwise treats independently.
const stack = [];
let originalScroll = null;
let lockedScroll = null;

// Explicitly wrap at the boundaries: some browsers move focus to their chrome
// without emitting focusin, so Bootstrap's focusin trap cannot recover it.
function wrapTab(event) {
    const top = stack.at(-1);
    if (event.key !== "Tab" || event.altKey || event.ctrlKey || event.metaKey || !top?.shown) return;
    const controls = SelectorEngine.focusableChildren(top.element);
    const first = controls[0];
    const last = controls.at(-1);
    const active = document.activeElement;
    if (!first || !top.element.contains(active) ||
        (event.shiftKey && (active === first || active === top.element)) ||
        (!event.shiftKey && active === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
        if (!first) top.element.focus();
    }
}

function snapshotScroll() {
    return [...new Set([
        document.body,
        ...document.querySelectorAll(".fixed-top, .fixed-bottom, .is-fixed, .sticky-top")
    ])].map((element) => ({
        element,
        properties: ["overflow", "padding-right", "margin-right"].map((property) => ({
            property,
            value: element.style.getPropertyValue(property),
            priority: element.style.getPropertyPriority(property),
            data: element.getAttribute(`data-bs-${property}`)
        }))
    }));
}

function restoreScroll(snapshot) {
    for (const { element, properties } of snapshot || []) {
        for (const { property, value, priority, data } of properties) {
            if (value) element.style.setProperty(property, value, priority);
            else element.style.removeProperty(property);
            if (data === null) element.removeAttribute(`data-bs-${property}`);
            else element.setAttribute(`data-bs-${property}`, data);
        }
    }
}

function synchronize() {
    const top = stack.at(-1);
    for (const entry of stack) {
        const active = entry === top;
        entry.element.inert = !active;
        if (active) {
            entry.element.removeAttribute("aria-hidden");
            entry.element.setAttribute("aria-modal", "true");
        } else {
            entry.trap.deactivate();
            entry.element.setAttribute("aria-hidden", "true");
            entry.element.removeAttribute("aria-modal");
        }
    }
    if (top?.shown) top.trap.activate();
}

export function registerModal(element) {
    if (stack.some((entry) => entry.element === element)) return;
    if (!stack.length) {
        originalScroll = snapshotScroll();
        document.addEventListener("keydown", wrapTab, true);
    }
    const backdrops = new Set(document.querySelectorAll(".modal-backdrop"));
    const entry = {
        element,
        trap: new FocusTrap({ trapElement: element, autofocus: false }),
        shown: false,
        zIndex: element.style.zIndex,
        inert: element.inert,
        previousBackdrops: backdrops,
        backdrop: null
    };
    stack.push(entry);
    element.style.zIndex = String(1055 + (stack.length - 1) * 20);
    synchronize();
    // Bootstrap appends its backdrop synchronously after show.bs.modal.
    queueMicrotask(() => {
        if (!stack.includes(entry)) return;
        entry.backdrop = [...document.querySelectorAll(".modal-backdrop")]
            .find((backdrop) => !backdrops.has(backdrop) &&
                !stack.some((owner) => owner !== entry && owner.backdrop === backdrop));
        if (entry.backdrop) entry.backdrop.style.zIndex = String(Number(element.style.zIndex) - 5);
        if (!lockedScroll) lockedScroll = snapshotScroll();
    });
}

export function modalShown(element) {
    const entry = stack.find((item) => item.element === element);
    if (entry) entry.shown = true;
    synchronize();
}

export function releaseModal(element) {
    const index = stack.findIndex((entry) => entry.element === element);
    if (index < 0) return;
    const [entry] = stack.splice(index, 1);
    entry.trap.deactivate();
    element.style.zIndex = entry.zIndex;
    element.inert = entry.inert;
    if (stack.length) {
        restoreScroll(lockedScroll);
        document.body.classList.add("modal-open");
    } else {
        restoreScroll(originalScroll);
        document.body.classList.remove("modal-open");
        originalScroll = null;
        lockedScroll = null;
        document.removeEventListener("keydown", wrapTab, true);
    }
    synchronize();
}

export function topModal() {
    return stack.at(-1)?.element;
}

export function modalBackdrop(element) {
    const entry = stack.find((item) => item.element === element);
    if (!entry) return null;
    return entry.backdrop || [...document.querySelectorAll(".modal-backdrop")]
        .find((backdrop) => !entry.previousBackdrops.has(backdrop) &&
            !stack.some((owner) => owner !== entry && owner.backdrop === backdrop));
}
